import { inject, Injectable } from '@angular/core';
import type { ScheduleRecommendation } from '@app/+onboarding/import/onboarding-import.models';
import { AiOnboardingApiService } from '@app/shared/ai/ai-onboarding-api.service';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { firstValueFrom } from 'rxjs';

import type { ReconciledEntry, ReconciliationSource } from './onboarding-draft-schedule.models';
import { toReconciledEntry, toWeekdayFlags } from './onboarding-draft-schedule.reconciler';

const DRAFT_SCHEDULE_ENDPOINT = 'onboarding/draft_schedule';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * The "3rd pass": binds the loosely-worded schedule recommendation to the teams
 * / shift templates / employees that were ACTUALLY created during onboarding.
 * The matching runs server-side — the backend owns the prompt and output schema,
 * fetches the account's teams / shift templates / employees from the database and
 * re-validates every returned id against them — so this service forwards only the
 * recommendation and enriches the bound rows locally (with department/times/names,
 * from the same created entities) for the draft overlay.
 *
 * Best-effort: returns [] on any failure so the caller can fall back to the
 * deterministic matcher.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingDraftScheduleAiService {
  #api = inject(AiOnboardingApiService);
  #feature = inject(FeatureService);

  async reconcile(recommendation: ScheduleRecommendation, source: ReconciliationSource): Promise<ReconciledEntry[]> {
    if (!this.#feature.isEnabled(featureFlag.TMP_AI_ONBOARDING)) {
      return [];
    }

    if (!recommendation.length || source.teams.length === 0 || source.shifts.length === 0) {
      return [];
    }

    try {
      // Only the recommendation is sent — the backend reads the account's teams,
      // shift templates and employees from the database itself. Best-effort: the
      // client already suppresses the global toast, and any failure falls back to
      // the deterministic matcher and stays silent for the user.
      const result = await firstValueFrom(
        this.#api.post<{ entries?: unknown }>(DRAFT_SCHEDULE_ENDPOINT, { recommendation }),
      );

      return this.#bind(Array.isArray(result?.entries) ? result.entries : [], source);
    } catch {
      return [];
    }
  }

  #bind(entries: unknown[], source: ReconciliationSource): ReconciledEntry[] {
    const teamById = new Map(source.teams.filter((team) => !!team.id).map((team) => [team.id, team]));
    const shiftById = new Map(source.shifts.map((shift) => [shift.id, shift]));
    const employeeById = new Map(
      source.employees.filter((employee) => !!employee.id).map((employee) => [employee.id, employee]),
    );

    return entries.reduce<ReconciledEntry[]>((bound, raw) => {
      // The backend response is provider-derived; validate each row field-by-field
      // (no up-front cast) so one malformed entry is skipped rather than throwing
      // and voiding the batch.
      if (!isRecord(raw)) {
        return bound;
      }

      const team = teamById.get(String(raw['teamId'] ?? ''));
      const shift = shiftById.get(String(raw['shiftId'] ?? ''));
      const days = toWeekdayFlags(raw['days']);

      if (!team?.id || !shift || days.length === 0) {
        return bound;
      }

      const userId = typeof raw['userId'] === 'string' ? raw['userId'] : '';
      const matched = userId ? employeeById.get(userId) : undefined;
      // Narrow to the { id, name } shape toReconciledEntry expects: the source
      // employee's id is optional, and the map values keep that optional type even
      // though it was built from id-bearing rows.
      const employee =
        matched?.id != null
          ? { id: matched.id, name: matched.name || `${matched.first_name} ${matched.last_name}`.trim() }
          : null;

      bound.push(toReconciledEntry({ id: team.id, name: team.name }, shift, employee, days));

      return bound;
    }, []);
  }
}
